import { FloatingFocusManager, FloatingPortal, useDismiss, useFloating, useId, useInteractions, useRole } from '@floating-ui/react'
import { AnimatePresence, motion } from 'motion/react'
import { ReactNode, useRef } from 'react'

import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { AstraHeading } from '../Typography/AstraHeading/AstraHeading'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'

export type AstraAlertDialogProps = {
    isOpen: boolean
    title: string
    description: ReactNode
    cancelButtonLabel: string
    confirmButtonLabel: string
    onCancel?: () => void
    onConfirm?: () => void
}

export const AstraAlertDialog = ({ isOpen, title, description, cancelButtonLabel, confirmButtonLabel, onCancel, onConfirm }: AstraAlertDialogProps) => {
    const titleId = useId()
    const descriptionId = useId()
    const cancelButtonRef = useRef<HTMLButtonElement>(null)

    const { refs, context } = useFloating({
        open: isOpen,
        onOpenChange: open => {
            if (!open) onCancel?.()
        },
    })

    const dismiss = useDismiss(context, {
        outsidePress: false,
    })
    const role = useRole(context, { role: 'alertdialog' })
    const { getFloatingProps } = useInteractions([dismiss, role])

    return (
        <AnimatePresence>
            {isOpen && (
                <FloatingPortal>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.1 }}
                        className="fixed inset-0 z-elevated bg-black/50"
                        onPointerDown={e => e.stopPropagation()}
                    />
                    <FloatingFocusManager context={context} modal initialFocus={cancelButtonRef}>
                        <div
                            ref={refs.setFloating}
                            className="fixed left-1/2 top-1/2 z-elevated max-h-[85vh] w-[90vw] max-w-[400px] -translate-x-1/2 -translate-y-1/2 transform outline-none"
                            {...getFloatingProps({
                                'aria-labelledby': titleId,
                                'aria-describedby': descriptionId,
                                onPointerDown: e => e.stopPropagation(),
                            })}
                        >
                            <motion.div
                                transition={{ ease: 'linear' }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex flex-col gap-4 rounded-md bg-surface-base p-6 shadow-dialog"
                            >
                                <div className="flex flex-col gap-2">
                                    <AstraHeading as="h2" id={titleId}>
                                        {title}
                                    </AstraHeading>
                                    <div id={descriptionId}>
                                        {typeof description === 'string' ? (
                                            <AstraParagraph weight="regular" variant="body">
                                                {description}
                                            </AstraParagraph>
                                        ) : (
                                            description
                                        )}
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 rounded-b">
                                    <AstraButton ref={cancelButtonRef} onClick={onCancel} variant="secondary" size="medium" label={cancelButtonLabel} />
                                    <AstraButton
                                        onClick={onConfirm}
                                        variant="critical"
                                        size="medium"
                                        data-test="alert-dialog-confirm"
                                        label={confirmButtonLabel}
                                    />
                                </div>
                            </motion.div>
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </AnimatePresence>
    )
}
